import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { isActivePatronRequestSeries } from "@/features/BookingParts/components/BookingInfo/isActivePatronRequestSeries";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { canAddMemberHold } from "@/utils/memberHolds/canAddMemberHold";

import { initializeEditBookingModalAtom } from "../../atoms/actions/initializeEditBookingModalAtom/initializeEditBookingModalAtom";
import { PatronEditBooking } from "../../types";
import { useInitializeEditBookingModal_Booking$key } from "./__generated__/useInitializeEditBookingModal_Booking.graphql";

type Props = {
  bookingRef: useInitializeEditBookingModal_Booking$key;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
};
export const useInitializeEditBookingModal = ({
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
}: Props) => {
  const booking = useFragment(
    useInitializeEditBookingModalFragmentString,
    bookingRef
  );
  const initializeEditBookingModal = useSetAtom(initializeEditBookingModalAtom);
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();
  const hasPendingRecurrence = !!route.params?.params?.pendingRecurrence;
  const defaultCustomerTypeId =
    booking.startSlot?.club?.defaultCustomerType?.id ?? null;

  const isRequestBooking = booking.startSlot?.__typename === "QueueSlot";
  const remainingSeriesBookingCount =
    booking.__typename === "StandardBooking" ||
    booking.__typename === "LessonBooking"
      ? booking.seriesCount ?? 0
      : 0;
  const slotCapacity = booking.startSlot?.capacity ?? 0;
  const slotUsedCapacity = isRequestBooking
    ? booking.rounds?.count ?? 0
    : booking.startSlot?.usedCapacity ?? 0;
  const remainingSlotCapacity = slotCapacity - slotUsedCapacity;

  const ownerClientId = booking.ownerClient?.id ?? "";

  const sourceDate = booking.startSlot?.agenda?.date ?? "";

  const isDurationBasedFacility =
    booking.startSlot?.agenda?.facility?.type !== "GOLF_COURSE";

  const isLessonBooking = booking.__typename === "LessonBooking";

  const indexedBookingIndexes =
    booking.rounds?.edges?.flatMap((edge) => {
      const bookingIndex = edge?.node?.bookingIndex;
      return bookingIndex == null ? [] : [bookingIndex];
    }) ?? [];
  const requestGroupCount =
    indexedBookingIndexes.length > 0
      ? Math.max(...indexedBookingIndexes) + 1
      : null;

  const startSlotId = booking.startSlot?.id;
  const courseId = booking.startSlot?.course?.id;

  const isAlreadyPaid =
    (booking.__typename === "StandardBooking" &&
      booking.paymentStatus === "PAID") ||
    booking.rounds?.edges?.some(
      (round) =>
        round?.node?.__typename === "CustomerRound" &&
        round.node.paymentStatus === "PAID"
    );

  const memberHoldEnabled =
    booking.startSlot?.agenda?.facility?.memberHoldEnabled;
  const canAddMemberHoldForBooking = isRequestBooking
    ? Boolean(memberHoldEnabled)
    : canAddMemberHold({
        memberHoldEnabled,
        memberHoldDeadlineHours:
          booking.startSlot?.agenda?.facility?.memberHoldDeadlineHours,
        bookingDate: booking.startSlot?.agenda?.date,
        bookingTime: booking.startSlot?.time,
      });

  const mappedBooking: PatronEditBooking.Booking = useMemo(() => {
    return {
      id: booking.id,
      rateId: booking.rate?.id ?? null,
      status: booking.status ?? "DRAFT",
      durationMins: booking.durationMins ?? null,
      startSlotId: startSlotId ?? null,
      courseId,
      copyOverrides: booking.startSlot?.agenda?.facility?.copyOverrides,
      isPrivate:
        booking.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false,
      request: booking.request ?? undefined,
      isRequestBooking,
      // A fulfilled occurrence keeps its bookingSeries but is no longer a queue-backed
      // request, so THIS_AND_FOLLOWING must not be offered for it. A leftover single
      // request after later series occurrences were removed also must not offer it.
      isRecurringRequest:
        isRequestBooking &&
        isActivePatronRequestSeries({
          hasSeriesRecurrence: !!booking.bookingSeries?.recurrenceType,
          remainingBookingCount: remainingSeriesBookingCount,
        }),
      // Draft + pendingRecurrence means the series has not been submitted yet.
      // Edit Request should persist this draft only and return to BookingSummary
      // so previewPatronRequestSeries can refresh conflicts.
      isUnsavedSeriesPreview:
        isRequestBooking && booking.status === "DRAFT" && hasPendingRecurrence,
      recurringRequestSeries: recurringRequestSeriesFromBooking(
        isRequestBooking,
        booking.bookingSeries,
        sourceDate,
        booking.bookingSeries?.bookings?.edges?.flatMap((edge) => {
          const date = edge?.node?.startSlot?.agenda?.date;
          return date ? [date] : [];
        }) ?? []
      ),
      isAlreadyPaid,
      bookingRates:
        booking.startSlot?.bookingRates?.map((rate) => ({
          id: rate?.rate?.id ?? "",
          name: rate?.rate?.name ?? "",
          isLocked:
            (rate?.rate?.visibility === "QUALIFYING" &&
              !rate?.rate?.appliesToMembershipType) ||
            rate?.rate?.visibility === "NONE",
        })) ?? [],
      type: isLessonBooking
        ? "Lesson"
        : isDurationBasedFacility
        ? "Duration"
        : "Golf",
      canAddMemberHold: canAddMemberHoldForBooking,
      groupCapacity: booking.startSlot?.capacity ?? null,
      requestGroupCount,
      maxRequestGroupCount:
        booking.request?.bookingRule?.maxRequestCount ?? null,
      ...(isLessonBooking
        ? {
            lessonTypeId: booking.lessonType?.id ?? null,
            instructors:
              booking.instructors?.edges?.map((edge) => edge?.node?.id ?? "") ??
              [],
          }
        : {}),
      rounds:
        booking.rounds?.edges?.flatMap((edge, index) => {
          const round = edge?.node;
          if (!round) return [];

          const player: PatronEditBooking.RoundPlayer = round.player
            ? (() => {
                const sharedPlayerFields = {
                  id: round.player.id,
                  firstName: round.player.firstName ?? "",
                  lastName: round.player.lastName ?? "",
                  profileImagePublicId:
                    round.player.profileImage?.publicId ?? null,
                  memberGroups:
                    round.player.memberGroups?.flatMap((g) =>
                      g?.id ? [g.id] : []
                    ) ?? [],
                };

                if (round.__typename === "CustomerRound") {
                  return {
                    ...sharedPlayerFields,
                    clientType: "customer",
                    membershipTypeId:
                      round.player.membershipType?.id ??
                      defaultCustomerTypeId ??
                      "",
                  };
                }

                if (round.__typename === "GuestRound") {
                  return {
                    ...sharedPlayerFields,
                    clientType: "guest",
                  };
                }

                return {
                  ...sharedPlayerFields,
                  clientType: "member",
                  membershipTypeId:
                    round.player.membershipType?.id ?? undefined,
                };
              })()
            : round.__typename === "MemberRound"
            ? {
                id: null,
                clientType: "memberHold",
                firstName: null,
                lastName: null,
              }
            : round.__typename === "CustomerRound"
            ? {
                id: null,
                clientType: "unknownCustomer",
                firstName: null,
                lastName: null,
              }
            : {
                id: null,
                clientType: "unknownGuest",
                firstName: null,
                lastName: null,
              };

          let editingRound: PatronEditBooking.Round;

          if (isDurationBasedFacility) {
            const durationRound: PatronEditBooking.DurationRound = {
              localId: round.id,
              serverId: round.id,
              index,
              bookingIndex: round.bookingIndex,
              player,

              isOwner: !!(
                round.player?.id && round.player?.id === ownerClientId
              ),
              isNonPlayingHost: round.nonPlayingHost ?? false,
              type: "Duration",
              paymentStatus: round.paymentStatus,
              manualPaymentStatus: round.manualPaymentStatus ?? "AUTO_CHARGE",
              hostId: round.host?.id ?? null,
              secondaryTransportationType: round.secondaryTransportationType
                ? {
                    id: round.secondaryTransportationType.id,
                  }
                : null,
              rate: round.roundRate?.rate
                ? {
                    id: round.roundRate.rate.id,
                    name: round.roundRate.rate.name,
                    isLocked:
                      (round.roundRate.rate.visibility === "QUALIFYING" &&
                        !round.roundRate.appliesToMembershipType) ||
                      round.roundRate.rate.visibility === "NONE",
                    totalDurationPrice: undefined,
                    visibility: round.roundRate.rate.visibility,
                  }
                : null,
            };
            editingRound = durationRound;
          } else {
            const golfRound: PatronEditBooking.GolfRound = {
              localId: round.id,
              serverId: round.id,
              index,
              bookingIndex: round.bookingIndex,
              player,
              isOwner: !!(
                round.player?.id && round.player?.id === ownerClientId
              ),
              isNonPlayingHost: round.nonPlayingHost ?? false,
              type: "Golf",
              hostId: round.host?.id ?? null,
              paymentStatus: round.paymentStatus,
              manualPaymentStatus: round.manualPaymentStatus ?? "AUTO_CHARGE",
              courseLayout: round.courseLayout?.id
                ? {
                    id: round.courseLayout.id,
                    holeCount: round.courseLayout?.holeCount ?? "EIGHTEEN",
                  }
                : null,
              transportationType: round.transportationType
                ? {
                    id: round.transportationType.id,
                  }
                : null,
              secondaryTransportationType: round.secondaryTransportationType
                ? {
                    id: round.secondaryTransportationType.id,
                  }
                : null,
              roundRate: round.roundRate?.rate
                ? {
                    id: round.roundRate.rate.id,
                    name: round.roundRate.rate.name,
                    nineHolePrice: round.roundRate.nineHolePrice ?? undefined,
                    eighteenHolePrice:
                      round.roundRate.eighteenHolePrice ?? undefined,
                    isLocked:
                      (round.roundRate.rate.visibility === "QUALIFYING" &&
                        !round.roundRate.appliesToMembershipType) ||
                      round.roundRate.rate.visibility === "NONE",
                    dynamicOverridden: round.roundRate.rate.dynamicOverridden,
                    dynamicEnabled: round.roundRate.rate.dynamicEnabled,
                    visibility: round.roundRate.rate.visibility,
                  }
                : null,
              bookedRate: round.bookedRate?.rate
                ? {
                    id: round.bookedRate.rate.id,
                    name: round.bookedRate.rate.name,
                    nineHolePrice: round.bookedRate.nineHolePrice ?? undefined,
                    eighteenHolePrice:
                      round.bookedRate.eighteenHolePrice ?? undefined,
                    isLocked:
                      (round.bookedRate.rate.visibility === "QUALIFYING" &&
                        !round.bookedRate.appliesToMembershipType) ||
                      round.bookedRate.rate.visibility === "NONE",
                    dynamicOverridden: round.bookedRate.rate.dynamicOverridden,
                    dynamicEnabled: round.bookedRate.rate.dynamicEnabled,
                  }
                : null,
            };
            editingRound = golfRound;
          }

          return editingRound ? [editingRound] : [];
        }) ?? [],
    };
  }, [
    booking.id,
    booking.rate?.id,
    booking.status,
    booking.durationMins,
    booking.request,
    booking.bookingSeries,
    remainingSeriesBookingCount,
    sourceDate,
    booking.startSlot?.bookingRates,
    booking.startSlot?.agenda?.facility?.copyOverrides,
    booking.startSlot?.agenda?.facility?.club?.supportsPrivate,
    booking.lessonType?.id,
    booking.instructors?.edges,
    booking.rounds?.edges,
    startSlotId,
    isAlreadyPaid,
    isLessonBooking,
    isRequestBooking,
    isDurationBasedFacility,
    hasPendingRecurrence,
    defaultCustomerTypeId,
    ownerClientId,
    canAddMemberHoldForBooking,
    courseId,
    booking.startSlot?.capacity,
    requestGroupCount,
  ]);

  useEffect(() => {
    if (!startSlotId) return;
    initializeEditBookingModal({
      booking: mappedBooking,
      defaultMembershipTypeId: defaultCustomerTypeId,
      remainingSlotCapacity,
      currentSessionMemberShipTypeId,
      currentSessionUserClientId,
      originalBookingOwnerClientId: ownerClientId,
      startSlotId,
    });
  }, [
    initializeEditBookingModal,
    mappedBooking,
    defaultCustomerTypeId,
    remainingSlotCapacity,
    currentSessionMemberShipTypeId,
    currentSessionUserClientId,
    ownerClientId,
    startSlotId,
  ]);
};

const recurrenceTypeFromSeries = (
  value: string | null | undefined
): PatronEditBooking.RecurringRequestSeriesInfo["recurrenceType"] | null => {
  if (value === "DAY" || value === "WEEK" || value === "MONTH") {
    return value;
  }

  return null;
};

const endConditionFromSeries = (
  value: string | null | undefined
): PatronEditBooking.RecurringRequestSeriesInfo["endCondition"] =>
  value === "ON_DATE" ? "ON_DATE" : "AFTER_OCCURRENCES";

const recurringRequestSeriesFromBooking = (
  isRequestBooking: boolean,
  series:
    | {
        recurrenceType?: string | null;
        repeatInterval?: number | null;
        endCondition?: string | null;
        effectiveEndDate?: string | null;
        requestedOccurrenceCount?: number | null;
      }
    | null
    | undefined,
  sourceDate: string,
  seriesBookingDates: string[]
): PatronEditBooking.RecurringRequestSeriesInfo | undefined => {
  const recurrenceType = recurrenceTypeFromSeries(series?.recurrenceType);

  if (!isRequestBooking || !recurrenceType) {
    return undefined;
  }

  return {
    recurrenceType,
    repeatInterval: series?.repeatInterval ?? 1,
    endCondition: endConditionFromSeries(series?.endCondition),
    effectiveEndDate: series?.effectiveEndDate,
    requestedOccurrenceCount: series?.requestedOccurrenceCount,
    sourceDate,
    seriesBookingDates,
  };
};

const useInitializeEditBookingModalFragmentString = graphql`
  fragment useInitializeEditBookingModal_Booking on Booking {
    id
    __typename
    bookingSeries {
      recurrenceType
      repeatInterval
      endCondition
      effectiveEndDate
      requestedOccurrenceCount
      bookings(first: 366, orderBy: [{ field: DATE_TIME, direction: DESC }]) {
        edges {
          node {
            id
            startSlot {
              agenda {
                date
              }
            }
          }
        }
      }
    }
    startSlot {
      __typename
    }
    ... on StandardBooking {
      seriesCount
      status
      paymentStatus
      request {
        time
        minTime
        maxTime
        bookingRule {
          ... on RequestRule {
            maxRequestCount
          }
        }
      }
      durationMins
      startSlot {
        ... on TimeSlot {
          capacity(bookingType: $bookingType)
          course {
            id
          }
        }
        ... on QueueSlot {
          capacity(bookingType: $bookingType)
        }
      }
    }
    rate {
      id
    }
    ... on LessonBooking {
      seriesCount
      request {
        time
        minTime
        maxTime
        bookingRule {
          ... on RequestRule {
            maxRequestCount
          }
        }
      }
      durationMins
      lessonType {
        id
      }
      instructors {
        edges {
          node {
            id
          }
        }
      }
      startSlot {
        ... on TimeSlot {
          capacity(bookingType: $bookingType)
          usedCapacity
        }
        ... on QueueSlot {
          capacity(bookingType: $bookingType)
        }
      }
    }
    ownerClient {
      ... on Customer {
        id
      }
      ... on Member {
        id
      }
    }
    startSlot {
      id
      agenda {
        date
        facility {
          type
          copyOverrides {
            lessonPlayer {
              singular
            }
            bookingPlayer {
              singular
            }
          }
          memberHoldEnabled
          memberHoldDeadlineHours
          club {
            supportsPrivate
          }
        }
      }
      ... on TimeSlot {
        time
        usedCapacity
        bookingRates(
          highestPrecedentOnly: false
          type: CUSTOMER
          visibleToMembershipTypeId: $membershipTypeId
        ) {
          rate {
            id
            name
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
        }
      }
      club {
        defaultCustomerType {
          id
        }
      }
    }
    rounds {
      count
      edges {
        node {
          id
          bookingIndex
          __typename
          transportationType {
            id
          }
          secondaryTransportationType {
            id
          }
          manualPaymentStatus
          bookedRate {
            appliesToMembershipType
            eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
            nineHolePrice: totalGolfPrice(holeCount: NINE)
            rate {
              id
              name
              visibility
              ... on CustomerRate {
                dynamicEnabled
                dynamicOverridden
              }
            }
          }
          roundRate {
            appliesToMembershipType
            eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
            nineHolePrice: totalGolfPrice(holeCount: NINE)
            rate {
              id
              name
              visibility
              ... on CustomerRate {
                dynamicEnabled
                dynamicOverridden
              }
            }
          }
          courseLayout {
            id
            holeCount
          }
          player {
            id
            firstName
            lastName
            ... on Customer {
              membershipType {
                id
              }
            }
            ... on Member {
              membershipType {
                id
              }
              memberGroups {
                id
              }
            }
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          paymentStatus
          ... on MemberRound {
            nonPlayingHost
          }
          ... on GuestRound {
            host {
              id
            }
          }
        }
      }
    }
  }
`;
